import * as signalR from "@aspnet/signalr";
import { createAction } from "redux-actions";
import { Thunk } from ".";
import * as abstractActions from "./AbstractActions";
import * as adminEventActions from "./AdminEventActions";
import * as navActions from "./NavigationActions";
import * as pageActions from "./PageActions";
import Settings from "../settings";

const prefix = "DB_SUBS_";

export class Constants {
    public static INITIALIZED = prefix + "INITIALIZED";
    public static WEBSOCKET_CONNECTED = prefix + "WEBSOCKET_CONNECTED";
    public static WEBSOCKET_DISCONNECTED = prefix + "WEBSOCKET_DISCONNECTED";
}

type WebsocketConnectedPayload = signalR.HubConnection;

export const init = (): Thunk => async (dispatch, getState) => {
    // if SSR, don't do anything here
    if (typeof window === "undefined")
        return;

    let connection = getState().dbSubscriptions.websocket;

    // if there is an existing connection, stop it
    if (connection)
        await connection.stop();

    // return if not connected
    if (!getState().conn.connected)
        return;

    try {
        connection = new signalR.HubConnectionBuilder()
            .withUrl(`${Settings.ServicesUrl}/hubs/subscriptions`)
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // connection = new signalR.HubConnection(`${Settings.ServicesUrl}/hubs/subscriptions`);
        connection.start().then((obj) => {
            // connection success
            if (connection) {
                dispatch(websocketConnected(connection));

                dispatch(initNavSubscription(connection));
                dispatch(abstractActions.resetEventAbtractsSubscriptions());
                dispatch(abstractActions.resetUserAbtractsSubscriptions());
                dispatch(adminEventActions.resetEventSubscriptions());
                dispatch(pageActions.resetDbSubscriptions());

                connection.onclose((e) => {
                    console.log("Connection closed", e);
                    dispatch(onWebsocketDisconnected());
                });
            }
        }, (connErr) => {
            // error connecting
            console.warn("Error connecting", connErr);
            dispatch(onWebsocketDisconnected());
        });
    }
    catch (err) {
        console.warn("Error connecting to websocket server", err);
        await dispatch(onWebsocketDisconnected());
    }
};

export const initNavSubscription = (connection: signalR.HubConnection): Thunk => async (dispatch, getState) => {
    try {
        connection.on("NavigationUpdateReceived", () => {
            // Navigation updates received for this app
            // start timer to refetch navigation
            console.log("received updates. Will refetch nav shortly...");
            dispatch(navActions.startReqestNavigationTimer());
        });

        connection.on("NavigationSubscribed", (appId) => {
            // We are subscribed to navigation updates.
            // console.log("subscribed to navigation updates.");
        });

        connection.on("NavigationUnsubscribed", (appId) => {
            // We are unsubscribed from navigation updates.
            console.log("unsubscribed from navigation updates.");
        });

        // subscribe to nav changes for this app
        // await connection.invoke("NavigationUnsubscribe", Settings.AppID);
        await connection.invoke("NavigationSubscribe", Settings.AppID);
    }
    catch (err) {
        console.warn("Error in navigation subscriptions", err);
    }
};

/**
 * Subscribes to DB changes for a specific page
 * @param mediaId The page ID to subscribe
 */
export const subscribeToMediaProcessing = (mediaId: number): Thunk => async (dispatch, getState) => {
    const connection = getState().dbSubscriptions.websocket;
    if (!connection)
        return;

    try {
        connection.on("MediaProcessingComplete", (mId) => {
            // Media has finished processing
            console.log(`Media processing completed for ID ${mId}.`);
            dispatch(abstractActions.toggleMediaProcessing(parseInt(mId, 10), false));
        });

        connection.on("MediaProcessingSubscribed", (mId) => {
            // We are subscribed to media processing updates.
            console.log("subscribed to media processing updates for ID", mId);
        });

        connection.on("MediaProcessingUnsubscribed", (mId) => {
            console.log("unsubscribed from media processing updates for ID", mId);
        });

        // subscribe to nav changes for this app
        // await connection.invoke("PageUnsubscribe", pageId);
        await connection.invoke("MediaProcessingSubscribe", mediaId);
    }
    catch (err) {
        console.warn("Subscription error", mediaId, err);
    }
};

/**
 * Subscribes to DB changes for a specific page
 * @param pageId The page ID to subscribe
 */
export const subscribeToPage = (pageId: number): Thunk => async (dispatch, getState) => {
    const connection = getState().dbSubscriptions.websocket;
    if (!connection)
        return;

    try {
        connection.on("PageUpdateReceived", () => {
            // Pages updates received for this app
            // start timer to refetch page
            console.log(`received updates. Will refetch page ${pageId} shortly...`);
            dispatch(pageActions.startReqestPageTimer(pageId));
        });

        connection.on("PageSubscribed", (appId) => {
            // We are subscribed to page updates.
            dispatch(pageActions.onPageSubscribed(pageId));
            console.log("subscribed to page updates for ID", pageId);
        });

        connection.on("PageUnsubscribed", (appId) => {
            console.log("unsubscribed from page updates for ID", pageId);
        });

        // subscribe to nav changes for this app
        // await connection.invoke("PageUnsubscribe", pageId);
        await connection.invoke("PageSubscribe", pageId);
    }
    catch (err) {
        console.warn("Subscription error", pageId, err);
    }
};

/**
 * Try to reconneted to the websocket server after a certain timeout
 */
export const tryReconnectWebsocket = (): Thunk => async (dispatch, getState) => {
    setTimeout(() => {
        dispatch(init());
    }, 10000);
};

export const onWebsocketDisconnected = (): Thunk => async (dispatch, getState) => {
    dispatch(websocketDisconnected());
    dispatch(tryReconnectWebsocket());
};

export const toggleInitialized = createAction<boolean, boolean>(
    Constants.INITIALIZED, (isInitialized) => (isInitialized),
);

export const websocketConnected = createAction<WebsocketConnectedPayload, signalR.HubConnection>(
    Constants.WEBSOCKET_CONNECTED, (connection) => (connection),
);

export const websocketDisconnected = createAction(Constants.WEBSOCKET_DISCONNECTED);



// WEBPACK FOOTER //
// ./src/actions/DbSubscriptionActions.ts